/**
 * @description Admin layout wrapper with sidebar and content area
 *
 * Key features:
 * - Fixed sidebar (AdminSidebar) on the left
 * - Scrollable content area on the right
 * - Top header bar with breadcrumb-style page title
 * - Switch-into overlay banner (LYN-157): You · Super Admin → {CP} → {SO}
 * - Outlet for nested route rendering
 *
 * @dependencies
 * - AdminSidebar: Navigation sidebar
 * - OverlayProvider / useOverlay: session overlay tag
 * - react-router-dom: Outlet for nested routes, useLocation for breadcrumb
 *
 * @notes
 * - Content area is offset by sidebar width (ml-64)
 * - Light background (gray-50) for content, dark sidebar (slate-900)
 * - Header shows section > sub-page title derived from URL path
 * - /tenants list chrome is "Sales Orgs"; wizard/detail keep Tenants
 * - /channel-partners/:id/sales-orgs/new chrome is Create Sales Org (LYN-323)
 * - Overlay Exit clears the tag. Up retargets parent. Sign out is SA Wristband logout.
 * - AdminNavProvider lets TenantDetail publish channelPartnerId for sidebar highlight
 */

import { Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { AdminNavProvider } from '../../contexts/AdminNavContext';
import { OverlayProvider, formatOverlayBanner, useOverlay } from '../../contexts/OverlayContext';

/**
 * Derive page title from URL path for the header
 * Supports sub-paths: /integrations/gateways → "Integrations > Gateways"
 */
function getPageTitle(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];

  const sectionTitles = {
    tenants: 'Tenants',
    'channel-partners': 'Channel Partners',
    integrations: 'Integrations',
    settings: 'Settings',
    'audit-logs': 'Audit Logs',
  };

  const subTitles = {
    gateways: 'Gateways',
    email: 'Email',
    new: 'Create',
  };

  // List page chrome only — wizard/detail keep Tenants so TenantCreate titles stay.
  if (first === 'tenants' && !segments[1]) {
    return 'Sales Orgs';
  }

  if (
    first === 'channel-partners'
    && segments[2] === 'sales-orgs'
    && segments[3] === 'new'
  ) {
    return 'Channel Partners > Create Sales Org';
  }

  const section = sectionTitles[first] || 'Admin';
  const sub = segments[1] ? subTitles[segments[1]] : null;

  return sub ? `${section} > ${sub}` : section;
}

function OverlayBanner() {
  const { overlay, error, busy, stop, up } = useOverlay();
  const banner = formatOverlayBanner(overlay);

  if (!overlay && !error) {
    return null;
  }

  return (
    <div className="px-6 py-2.5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between gap-4">
      <div>
        {banner && (
          <p className="text-sm font-medium text-indigo-900">{banner}</p>
        )}
        {error && (
          <p className="text-xs text-red-600 mt-0.5">{error}</p>
        )}
      </div>
      {overlay && (
        <div className="flex items-center gap-2 shrink-0">
          {overlay.parent && (
            <button
              type="button"
              onClick={() => up()}
              disabled={busy}
              className="px-3 py-1 text-xs font-medium rounded-md border border-indigo-200 text-indigo-800 bg-white hover:bg-indigo-50 disabled:opacity-50"
            >
              Up
            </button>
          )}
          <button
            type="button"
            onClick={() => stop()}
            disabled={busy}
            className="px-3 py-1 text-xs font-medium rounded-md bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-50"
          >
            Exit
          </button>
        </div>
      )}
    </div>
  );
}

function AdminShell() {
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar />

      {/* Main content area */}
      <div className="ml-64">
        <OverlayBanner />
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6">
          <h2 id="page-title" className="text-lg font-semibold text-gray-900">{pageTitle}</h2>
        </header>

        {/* Page content */}
        <main className="p-6" aria-labelledby="page-title">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AdminLayout() {
  return (
    <OverlayProvider>
      <AdminNavProvider>
        <AdminShell />
      </AdminNavProvider>
    </OverlayProvider>
  );
}

export { formatOverlayBanner };
export default AdminLayout;
